import { useGetCountries } from "@/actions/hooks/useGetCountries.tsx";
import { UserInfoFI } from "@/components/HolderForm/UserInfoFI.ts";
import { PaymentFI } from "@/components/Payment/PaymentForm/enums/PaymentFI.ts";
import { getCardBrandImgURL } from "@/components/Payment/PaymentForm/getCardBrandImgURL.ts";
import { isInquiryPaymentType } from "@/helpers/isInquiryPaymentType.ts";
import { PaymentProviderEnum } from "@/helpers/types/types.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.tsx";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { Briefcase, CalendarDays, CreditCard, IdCard, Mail, MapPin, Phone, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const HolderAndPaymentOverview = () => {
  const { t } = useTranslation();
  const { countries } = useGetCountries();
  const { holder, cardType, cardLastDigits, expiryDate, paymentData, calculatedCartData } = useEngineStore(
    useShallow((state) => ({
      holder: state.reservationHolderData,
      cardType: state.cardType,
      cardLastDigits: state.cardLastDigits,
      expiryDate: state.cardExpireDate,
      paymentData: state.paymentData,
      calculatedCartData: state.calculatedCartData,
    })),
  );
  const isPaymentDisabled = isInquiryPaymentType(calculatedCartData) || !paymentData;
  const { paymentType } = useGetOrganization();

  return (
    <div className="holderAndPaymentInfo">
      <div className="holderAndPaymentInfo__holder">
        <h6>{t("holderAndPaymentOverview.holder")}</h6>
        <p className="holderAndPaymentInfo__row">
          <User size={18} />
          {holder?.[UserInfoFI.FirstName]} {holder?.[UserInfoFI.LastName]}
        </p>
        <p className="holderAndPaymentInfo__row">
          <MapPin size={18} />
          {holder?.[UserInfoFI.Address]}, {holder?.[UserInfoFI.City]},{" "}
          {countries.find((c) => c.iso3Code === holder?.[UserInfoFI.Country])?.name}
        </p>
        <p className="holderAndPaymentInfo__row">
          <Mail size={18} />
          {holder?.[UserInfoFI.Email]}
        </p>
        <p className="holderAndPaymentInfo__row">
          <Phone size={18} />
          {holder?.[UserInfoFI.Phone]}
        </p>
      </div>
      {holder?.[UserInfoFI.CompanyName] && (
        <div className="holderAndPaymentInfo__company">
          <h6>{t("holderAndPaymentOverview.company")}</h6>
          <p className={"holderAndPaymentInfo__row"}>
            <User size={18} />
            {holder?.[UserInfoFI.CompanyName]}
          </p>
          <p className="holderAndPaymentInfo__row">
            <MapPin size={18} />
            {holder?.[UserInfoFI.CompanyAddress]},{" "}
            {countries.find((c) => c.iso3Code === holder?.[UserInfoFI.CompanyCountry])?.name}
          </p>
          <p className="holderAndPaymentInfo__row">
            <Briefcase size={18} />
            {holder?.[UserInfoFI.CompanyVAT]}
          </p>
        </div>
      )}
      {!isPaymentDisabled && paymentType === PaymentProviderEnum.WsPayPer && (
        <div className="holderAndPaymentInfo__payment">
          <h6>{t("holderAndPaymentOverview.payment")}</h6>
          <p className="holderAndPaymentInfo__row">
            <IdCard size={18} />
            {paymentData?.[PaymentFI.HolderFirstName] ?? holder?.[UserInfoFI.FirstName]}{" "}
            {paymentData?.[PaymentFI.HolderLastName] ?? holder?.[UserInfoFI.LastName]}
          </p>
          <div className="holderAndPaymentInfo__payment--card-number">
            {getCardBrandImgURL(cardType!) !== "Unknown" ? (
              <img height={18} src={getCardBrandImgURL(cardType!)} alt={"card"} />
            ) : (
              <CreditCard size={18} />
            )}
            <p>**** **** **** {cardLastDigits}</p>
          </div>
          <p className="holderAndPaymentInfo__row">
            <CalendarDays size={18} />
            {t("holderAndPaymentOverview.cardExpires")} {expiryDate}
          </p>
        </div>
      )}
      {!isPaymentDisabled && paymentType === PaymentProviderEnum.WsPay && (
        <div className="holderAndPaymentInfo__paymentDirect">
          <h6>{t("holderAndPaymentOverview.payment")}</h6>
          <p className="holderAndPaymentInfo__row">
            <CreditCard size={18} />
            {t("creditCardPayment")}
          </p>
        </div>
      )}
    </div>
  );
};
